import React, { FC, useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';
import {
  EMPTY_ONBOARDING_VALIDATION_CONTEXT,
  OnboardingDraft,
  OnboardingValidationContext,
  ValidationIssue,
  validateOnboardingDraft,
} from '@intelligent-play-v2/domain';

import {
  postOnboardingCommit,
  postOnboardingFacilityImage,
  postOnboardingParse,
} from '~/api';
import { ContentContainer } from '~/components/layout';
import {
  OnboardingCommitPreview,
  OnboardingPasteStep,
  OnboardingReviewForm,
} from '~/components/admin/onboarding';
import { usePageTitle } from '~/hooks';
import { RoutesPath } from '~/types';

type Step = 'paste' | 'review' | 'preview';

/**
 * Onboard a new facility from a pasted form response.
 *
 * Three steps in one page: paste, review and amend, then a summary before committing.
 * Draft state is in memory only — a paste-to-commit session is minutes long, and draft
 * persistence is deliberately out of scope for this stage.
 */
export const AdminOnboardingPage: FC = () => {
  usePageTitle('Onboard facility - Intelligent Play');
  const history = useHistory();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>('paste');
  const [tsv, setTsv] = useState('');
  const [draft, setDraft] = useState<OnboardingDraft | null>(null);
  const [reviewedDraft, setReviewedDraft] = useState<OnboardingDraft | null>(
    null
  );
  /**
   * The overview image, held in memory like the rest of the draft. It is only sent to
   * the server after commit succeeds — if the facility is never created, nothing is
   * uploaded anywhere.
   */
  const [imageFile, setImageFile] = useState<File | null>(null);
  // one object URL per file, shared by the review and preview steps and revoked here
  const imagePreviewUrl = useMemo(
    () => (imageFile ? URL.createObjectURL(imageFile) : null),
    [imageFile]
  );
  useEffect(
    () => () => {
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
    },
    [imagePreviewUrl]
  );
  /*
   * The two kinds of issue the client cannot re-derive from the draft, kept apart because
   * they expire differently. Everything else is validated live against `context` as the
   * reviewer types.
   */
  /** What was wrong with the paste. Outlives an edit; cleared by a new parse. */
  const [parseIssues, setParseIssues] = useState<ValidationIssue[]>([]);
  /** What the server said when it refused a commit. Cleared when the draft is re-submitted. */
  const [commitIssues, setCommitIssues] = useState<ValidationIssue[]>([]);
  const standingIssues = [...parseIssues, ...commitIssues];
  const [context, setContext] = useState<OnboardingValidationContext>(
    EMPTY_ONBOARDING_VALIDATION_CONTEXT
  );
  const [matchedByPosition, setMatchedByPosition] = useState(false);
  const [templateSignatures, setTemplateSignatures] = useState<string[]>([]);

  const { mutate: parsePaste, isLoading: isParsing } = useMutation(
    postOnboardingParse,
    {
      onSuccess: response => {
        setDraft(response.draft);
        setParseIssues([...response.errors, ...response.warnings]);
        setCommitIssues([]);
        if (!response.context) {
          // an API older than this page: everything but the collision checks still works,
          // and commit re-validates against the database anyway
          // eslint-disable-next-line no-console
          console.warn(
            'onboarding/parse returned no validation context — id and name collisions ' +
              'will only be reported when you commit.'
          );
        }
        setContext(response.context ?? EMPTY_ONBOARDING_VALIDATION_CONTEXT);
        setMatchedByPosition(response.matchedByPosition);

        /*
         * Capture what the default schedules looked like straight from the server, so each
         * card can say it came from the template and the preview can tell whether the
         * reviewer ever touched them. Any pitch that came back with schedules was
         * pre-filled, and the whole set is the template — not just its first entry.
         */
        const prefilled = response.draft.pitches.find(
          pitch => pitch.maintenanceSchedules.length > 0
        );
        setTemplateSignatures(
          (prefilled?.maintenanceSchedules ?? []).map(schedule =>
            JSON.stringify(schedule)
          )
        );

        // an unreadable paste keeps the user on the paste step; there is nothing to review
        if (response.errors.length > 0 && response.draft.pitches.length === 0) {
          return;
        }
        setStep('review');
      },
      onError: (error: AxiosError<{ message?: string }>) => {
        toast.error(
          error.response?.data?.message ??
            'Could not read that paste. Please try again.'
        );
      },
    }
  );

  const { mutate: commitDraft, isLoading: isCommitting } = useMutation(
    postOnboardingCommit,
    {
      // react-query keeps isLoading true until this resolves, so the button spinner
      // covers the image upload too
      onSuccess: async response => {
        toast.success(`Facility ${response.facilityId} created`);

        if (imageFile) {
          try {
            await postOnboardingFacilityImage({
              facilityId: response.facilityId,
              file: imageFile,
            });
            toast.success('Overview image uploaded');
          } catch (error) {
            /*
             * The facility exists either way — surface the failure and move on rather
             * than blocking, since the image can still be placed in the bucket manually.
             */
            const message = (error as AxiosError<{ message?: string }>).response
              ?.data?.message;
            toast.error(
              message ??
                `The facility was created but its image failed to upload. Add facility${response.facilityId}.png to the smart-pitches-facility-images bucket manually.`,
              { duration: 10000 }
            );
          }
        }

        /*
         * The app-wide facility list is a react-query cache feeding the metadata store, so
         * without this the new facility is missing everywhere — including the page we are
         * about to redirect to — until the browser is reloaded. Every other admin page
         * that changes a facility invalidates the same key.
         */
        queryClient.invalidateQueries('facilities');

        history.push(
          RoutesPath.AdminFacility.replace(
            ':facilityId',
            String(response.facilityId)
          )
        );
      },
      onError: (
        error: AxiosError<{ message?: string; details?: string[] }>
      ) => {
        const data = error.response?.data;
        const details = data?.details ?? [];
        toast.error(data?.message ?? 'Onboarding failed. Nothing was saved.');
        if (details.length > 0) {
          /*
           * Surface them on the form so they can be fixed where they happened. These are
           * the server's word on state the client cannot see — an id claimed by another
           * insert during the review — so they stand until the reviewer submits again.
           */
          setCommitIssues(
            details.map(detail => {
              const [path, ...rest] = detail.split(': ');
              return rest.length > 0
                ? { path, message: rest.join(': '), severity: 'error' as const }
                : { path: '', message: detail, severity: 'error' as const };
            })
          );
          setStep('review');
        }
      },
    }
  );

  const startOver = (): void => {
    setStep('paste');
    setDraft(null);
    setReviewedDraft(null);
    setImageFile(null);
    setParseIssues([]);
    setCommitIssues([]);
  };

  /** The preview blocks on the same live validation the review form paints with. */
  const issuesFor = (
    candidate: OnboardingDraft
  ): { errors: ValidationIssue[]; warnings: ValidationIssue[] } => {
    const { errors, warnings } = validateOnboardingDraft(candidate, context);
    return {
      errors: [
        ...standingIssues.filter(issue => issue.severity === 'error'),
        ...errors,
      ],
      warnings: [
        ...standingIssues.filter(issue => issue.severity === 'warning'),
        ...warnings,
      ],
    };
  };

  const previewIssues = reviewedDraft
    ? issuesFor(reviewedDraft)
    : { errors: [], warnings: [] };

  const hasUntouchedDefaultSchedules = (candidate: OnboardingDraft): boolean =>
    candidate.pitches.some(pitch =>
      pitch.maintenanceSchedules.some(schedule =>
        templateSignatures.includes(JSON.stringify(schedule))
      )
    );

  return (
    <ContentContainer className="py-12.5">
      <div className="max-w-6xl">
        <p className="text-3xl font-semibold">Onboard facility</p>
        <p className="mt-1 text-base text-primary-500">
          Creates a new facility from a form response. Adding a pitch to a
          facility that already exists is not supported here yet.
        </p>

        <div className="mt-8">
          {step === 'paste' && (
            <OnboardingPasteStep
              tsv={tsv}
              onTsvChange={setTsv}
              onParse={() => parsePaste({ tsv })}
              isParsing={isParsing}
              errors={parseIssues.filter(issue => issue.severity === 'error')}
              matchedByPosition={matchedByPosition}
            />
          )}

          {step === 'review' && draft && (
            <OnboardingReviewForm
              draft={reviewedDraft ?? draft}
              standingIssues={standingIssues}
              context={context}
              templateSignatures={templateSignatures}
              imageFile={imageFile}
              imagePreviewUrl={imagePreviewUrl}
              onImageFileChange={setImageFile}
              onReviewed={values => {
                // the server's last word was about the draft as it was, not as it is now
                setCommitIssues([]);
                setReviewedDraft(values);
                setStep('preview');
              }}
              onStartOver={startOver}
            />
          )}

          {step === 'preview' && reviewedDraft && (
            <OnboardingCommitPreview
              draft={reviewedDraft}
              errors={previewIssues.errors}
              warnings={previewIssues.warnings}
              hasUntouchedDefaultSchedules={hasUntouchedDefaultSchedules(
                reviewedDraft
              )}
              imageFile={imageFile}
              imagePreviewUrl={imagePreviewUrl}
              onImageFileChange={setImageFile}
              isCommitting={isCommitting}
              onCommit={() => commitDraft({ draft: reviewedDraft })}
              onBack={() => setStep('review')}
            />
          )}
        </div>
      </div>
    </ContentContainer>
  );
};
